import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import { DialogService } from '@progress/kendo-angular-dialog';
import { GridComponent } from '@progress/kendo-angular-grid';
import {
  ContextMenuComponent,
  ContextMenuSelectEvent,
} from '@progress/kendo-angular-menu';
import {
  NotificationRef,
  NotificationService,
} from '@progress/kendo-angular-notification';
import {
  moreVerticalIcon,
  pencilIcon,
  SVGIcon,
} from '@progress/kendo-svg-icons';
import * as moment from 'moment';

import {
  catchError,
  combineLatest,
  debounceTime,
  filter,
  finalize,
  Observable,
  of,
  startWith,
  take,
  takeUntil,
} from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { RoutesConstantService } from 'src/app/services/routes-constant.service';
import { StudentService } from 'src/app/services/student.service';
import { AbstractForm } from 'src/app/shared/components/abstract-form';
import { ErrorMessages } from 'src/app/shared/errors/student-service-errors';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import { ISedmsFormModel } from 'src/app/shared/interfaces/form/sedms-form-model';
import { GeoClientResponse } from 'src/app/shared/interfaces/goat-address';
import {
  StudentActions,
  StudentInfo,
} from 'src/app/shared/interfaces/student-info';
import { User } from 'src/app/shared/models/user';
import { AuthStore } from 'src/app/stores/auth.store';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { StudentStore } from 'src/app/stores/student.store';
import { EiAddStudent } from '../../ei-transition/models/ei-add-student.model';
import { ParentGuardianDetailsResponse } from '../../verification-guardian-parent-child/models/parent-guardian-details-response.model';
import { CreateNewStudentLinkToParentModel } from '../models/create-and-link-new-student-models/create-new-student-link-to-parent-model';
import { NewStudentModel } from '../models/create-and-link-new-student-models/new-student-model';
import { ExistingStudents } from '../models/external-search-models/existing-student-model';
import { StudentDetails } from '../models/student-details-model';
import { StudentSearchModel } from '../models/student-search-model';
import { AddNewStudentFormModel } from '../models/student-search-new-student-models/add-new-student-form.model';
import { INewStudentReferenceData } from '../models/student-search-new-student-models/new-student-reference-data.model';
import { StudentSearchNewStudentRequest } from '../models/student-search-new-student-models/student-search-add-new-student.model';
import { StudentSearchSubmitNewStudentRequest } from '../models/student-search-new-student-models/student-search-submit-new-student.model';

@Component({
  selector: 'app-new-student',
  templateUrl: './new-student.component.html',
  styleUrls: ['./new-student.component.scss'],
  standalone: false,
})
export class NewStudentComponent extends AbstractForm<
  StudentSearchNewStudentRequest,
  StudentSearchNewStudentRequest,
  INewStudentReferenceData
> {
  @Input() type: string;
  @Input() parentGuardianDetails: ParentGuardianDetailsResponse;
  @Input() showDialog: boolean;
  @Input() student?: EiAddStudent;
  @Output() hideDialog: EventEmitter<void> = new EventEmitter<void>();
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
  @Input() isEISearch: boolean = false;
  @Input() newReferral: boolean = false;
  @ViewChild('dialog', { read: ElementRef })
  public createStudentDialog: ElementRef;
  form: FormGroup;
  formModel: ISedmsFormModel = AddNewStudentFormModel;
  todayDate: Date = new Date();
  threeYearsAgo: Date = new Date();
  user: User;
  isShowGrid: boolean = false;
  public loading: boolean = true;
  public format: FormatSettings = {
    displayFormat: 'MM/DD/YYYY',
    inputFormat: 'MM/DD/YYYY',
  };
  students: StudentInfo[] = [];
  duplicateGridData: ExistingStudents[];
  public pencilIcon: SVGIcon = pencilIcon;
  public isStudentSelected: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  public minDOB: Date = new Date(1950, 1, 1); // Disabled because we want to create a specific date and these numbers aren't random
  public maxDOB: Date = new Date();
  public show: string = '';
  public actionIcon: SVGIcon = moreVerticalIcon;
  existingNotification: NotificationRef;

  public duplicateStudent: StudentActions;
  public minPreschoolDob: Date = new Date();
  @ViewChild('gridmenu') public gridContextMenu: ContextMenuComponent;
  @ViewChild('grid') public grid: GridComponent;
  @ViewChildren('anchor') public anchorRefs!: QueryList<
    ElementRef<HTMLButtonElement>
  >;

  readonly validators: typeof Validators = Validators;
  readonly canSubmit: boolean = false;
  readonly newReferralStatus: number = 7;
  readonly nycIdLength: number = 6;
  readonly newYorkStateID: number = 33;
  readonly debounceTime: number = 300;
  readonly addressErrorMessageLength: number = 0;

  // Home borough ids, ids range from 1 to 5
  readonly bronxBoroughId: number = 1;
  readonly statenIslandBoroughId: number = 5;
  readonly outOfNewYorkCityId: number = 6;
  readonly outOfNewYorkSId: number = 7;

  // Years
  readonly twoYears: number = 2;
  readonly threeYears: number = 3;

  // Days
  readonly dayOne: number = 1;
  readonly dayFour: number = 4;

  // Months; starting from 0 based index to 11. January is 0 ... December is 11.
  readonly march: number = 2;
  readonly april: number = 3;
  readonly july: number = 6;
  readonly august: number = 7;
  readonly september: number = 8;
  readonly october: number = 9;

  constructor(
    cdRef: ChangeDetectorRef,
    route: ActivatedRoute,
    fb: FormBuilder,
    dialogService: DialogService,
    notificationService: NotificationService,
    public studentService: StudentService,
    private referenceStore: ReferenceStore,
    public commonService: CommonService,
    private authStore: AuthStore,
    public routesConstantService: RoutesConstantService,
    public router: Router,
    public studentStore: StudentStore
  ) {
    super(cdRef, route, fb, dialogService, notificationService);
    this.user = this.authStore.UserInfo;
    this.authStore.UserInfo$.subscribe((userInfo: User) => {
      this.user = new User(userInfo);
      this.setSchoolFields();

      this.threeYearsAgo.setFullYear(this.todayDate.getFullYear() - 2);
      this.minPreschoolDob = moment()
        .subtract(5, 'years')
        .startOf('day')
        .toDate();
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.referenceDataLoaded
      .pipe(
        filter((loaded: boolean) => loaded),
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        take(1)
      )
      .subscribe(() => {
        if (this.student) {
          // TODO: code needs to be refactored. Currently patches the value to a class instance of StudentSearchNewStudentRequest.Should be refactored to address individual fields.
          const studentData: StudentSearchNewStudentRequest =
            new StudentSearchNewStudentRequest(this.student);
          this.form.patchValue({
            ...studentData,
            streetNumber: studentData.houseNumber,
          });
          // this.form.patchValue({
          //   ...this.student,
          //   dob: new Date(this.student?.dateOfBirth),
          //   guardianFirstName: this.student?.parentDetails?.[0]?.firstName || null,
          //   guardianLastName: this.student?.parentDetails?.[0]?.lastName || null,
          // })
          this.form.updateValueAndValidity();
        }
      });
  }

  initEntity(): StudentSearchNewStudentRequest {
    return new StudentSearchNewStudentRequest();
  }

  initReferenceData([
    sex,
    borough,
    state,
    district,
    locations,
    relationship,
    language,
  ]: IReferenceData[][]): INewStudentReferenceData {
    relationship = relationship
      .filter(
        (type: IReferenceData) => type?.value !== null && type?.value !== 'NULL'
      )
      .sort((a: IReferenceData, b: IReferenceData) =>
        a.value.localeCompare(b.value)
      );
    return { sex, borough, state, district, locations, relationship, language };
  }

  initWatches(): void {
    if (this.type === 'createLinkStudent') {
      if (this.parentGuardianDetails) {
        const data: StudentDetails = new StudentDetails(
          this.parentGuardianDetails,
          this.referenceData
        );
        // TODO: code needs to be refactored.
        this.form.patchValue(data);
        this.cdRef.detectChanges();
      }
    }
    this.loading = false;
    combineLatest([
      this.form.get('homeBorough')?.valueChanges.pipe(
        startWith(this.form.get('homeBorough')?.value),
        catchError(() => of(''))
      ),
      this.form.get('streetNumber')?.valueChanges.pipe(
        startWith(this.form.get('streetNumber')?.value),
        catchError(() => of(''))
      ),
      this.form.get('streetName')?.valueChanges.pipe(
        startWith(this.form.get('streetName')?.value),
        catchError(() => of(''))
      ),
    ])
      .pipe(debounceTime(this.debounceTime), takeUntil(this.componentDestroyed))
      .subscribe(
        ([homeBorough, streetNumber, streetName]: [
          IReferenceData,
          string,
          string
        ]) => {
          if (
            homeBorough &&
            streetNumber &&
            streetName &&
            Number(homeBorough?.id) >= this.bronxBoroughId &&
            Number(homeBorough?.id) <= this.statenIslandBoroughId
          ) {
            this.commonService
              .getGOATAddress(
                homeBorough?.description,
                streetNumber,
                streetName
              )
              .pipe(takeUntil(this.componentDestroyed))
              .subscribe((response: GeoClientResponse) => {
                if (!response?.address?.message) {
                  this.form
                    .get('city')
                    .setValue(
                      response?.address?.uspsPreferredCityName?.trim() ?? ''
                    );
                  this.form.get('city').updateValueAndValidity();
                  this.form
                    .get('zip')
                    .setValue(response?.address?.zipCode?.trim() ?? '');
                  this.form.get('zip').updateValueAndValidity();
                  this.form
                    .get('state')
                    .setValue(
                      this.referenceData.state.find(
                        (data: IReferenceData) =>
                          data?.id === this.newYorkStateID,
                      ),
                    );
                  this.form.get('state').updateValueAndValidity();
                  this.form
                    .get('homeDistrict')
                    .setValue(
                      this.referenceData.district.find(
                        (data: IReferenceData) =>
                          data?.id ===
                          +response?.address?.communitySchoolDistrict?.trim(),
                      ),
                    );
                  this.form.get('homeDistrict').updateValueAndValidity();
                }
              });
          }
        },
      );

    this.form
      .get('homeBorough')
      ?.valueChanges.pipe(
        startWith(this.form.get('homeBorough')?.value),
        takeUntil(this.componentDestroyed),
      )
      .subscribe((value: IReferenceData) => {
        if (
          (value?.id as number) >= this.bronxBoroughId &&
          (value?.id as number) <= this.statenIslandBoroughId
        ) {
          this.form
            .get('state')
            .setValue(
              this.referenceData.state.find(
                (data: IReferenceData) => data?.id === this.newYorkStateID,
              ),
            );
          this.form.get('state').updateValueAndValidity();

          // If the home borough is within NYC boroughs, then home district becomes required. Otherwise, is not required and should be cleared.
          this.form.get('homeDistrict')?.enable();
          this.form.get('homeDistrict')?.setValidators([Validators.required]);
          this.form.get('homeDistrict')?.updateValueAndValidity();
        } else {
          this.form.get('homeDistrict')?.setValue(null);
          this.form.get('homeDistrict')?.disable();
          this.form.get('homeDistrict')?.clearValidators();
          this.form.get('homeDistrict')?.updateValueAndValidity();
        }
      });

  }

  override otherInitalizations(): void {
    this.setSchoolFields();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  postCancelConfirmation(): void { }

  postSubmitConfirmation(
    isSubmit: boolean,
    response: ApiResponse<NewStudentModel>
  ): void {
    this.notificationService.show({
      content: `New Student is created. Student ID is ${response?.value?.nycID}`,
      cssClass: 'notification-success custom-notification-position',
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'slide', duration: 60 },
      type: { style: 'success', icon: false },
      closable: true,
    });
    this.loading = false;
    this.resetForm();
    this.hideDialog.emit();
    if (this.type === 'createLinkStudent') {
      this.router.navigate([
        this.routesConstantService.routes.ParentPortalRequestsGrid,
      ]);
    }
  }
  public splitBeforeAndAfterFirstSpace(input: string): {
    before: string;
    after: string;
  } {
    const [before, ...rest]: string[] = (input || '').trim().split(' ');
    const after: string = rest.join(' ')?.trim();
    return { before, after };
  }

  saveDetails(isSubmit: boolean): Observable<StudentSearchNewStudentRequest> {
    this.loading = true;
    // Creating a new student
    if (this.type === 'createLinkStudent') {
      const formDetails: CreateNewStudentLinkToParentModel = {
        linkSk: this.parentGuardianDetails?.linkSk,
        guardianId: this.parentGuardianDetails?.guardianId,
        parentFname: this.form.get('guardianFirstName').value,
        parentLname: this.form.get('guardianLastName').value,
        parentSpokenLanguage: this.form.get('guardianSpokenLanguage').value
          ?.value,
        parentWrittenLanguage: this.form.get('guardianWrittenLanguage').value
          ?.value,
        parentCellPhone: this.form.get('guardianCellPhone').value,
        parentWorkPhone: this.form.get('guardianWorkPhone').value,
        parentEmail: this.form.get('guardianEmail').value,
        streetNumber: this.form.get('streetNumber').value,
        streetName: this.form.get('streetName').value,
        city: this.form.get('city').value,
        state: this.form.get('state').value?.value,
        zip: this.form.get('zip').value,
        childFname: this.form.get('firstName').value,
        childLname: this.form.get('lastName').value,
        childDob: this.form.get('dob').value,
        childGender: this.form.get('gender').value?.value,
        relationToChild: this.form.get('guardianRelationship').value?.value,
        homeBorough: this.form.get('homeBorough').value?.value,
        homeDistrict: this.form.get('homeDistrict').value?.value,
        schoolDistrict: this.form.get('homeDistrict').value?.value,
        reasonforReferral: this.parentGuardianDetails?.reasonforReferral,
        otherReferralReason: this.parentGuardianDetails?.otherReferralReason,
        // relationReq: this.parentGuardianDetails?.reasonforReferral,
        eiService: this.parentGuardianDetails?.eiService,
      };
      return this.studentService.addStudentandLinkWithParent(formDetails).pipe(
        finalize(() => {
          this.loading = false;
        })
      );
    } else {
      // Construct student object, to check if student already exists
      const submitNewStudentDetails: StudentSearchSubmitNewStudentRequest =
        new StudentSearchSubmitNewStudentRequest({
          ...this.form.value,
          isLocked: false,
          isSubmit: isSubmit,
        });
      submitNewStudentDetails.bypassDuplicateCheck = this.isShowGrid;
      submitNewStudentDetails.addedBy = this.user?.email ?? this.user?.username;
      return new Observable<StudentSearchNewStudentRequest>(() => {
        this.studentService.addNewStudent(submitNewStudentDetails).subscribe({
          next: (response: ApiResponse<NewStudentModel>) => {
            if (
              response.error?.startsWith(
                ErrorMessages.STUDENT_ALREADY_EXISTS
              ) ||
              response.error?.startsWith(
                ErrorMessages.STUDENT_REGISTRATION_FAILED
              )
            ) {
              this.notificationService.show({
                content: response.error,
                cssClass: 'notification-error',
                hideAfter: 5000,
                position: { horizontal: 'center', vertical: 'top' },
                animation: { type: 'slide', duration: 200 },
                type: { style: 'error', icon: true },
              });

              // Construct payload to search in MSI
              const payload: StudentSearchModel = {
                firstName: submitNewStudentDetails.firstName,
                lastName: submitNewStudentDetails.lastName,
                dob: submitNewStudentDetails.dob
                  ? moment(submitNewStudentDetails.dob).format('YYYY-MM-DD')
                  : null,
              };
              this.studentService.searchMSI(payload).subscribe({
                next: (msiResponse: ExistingStudents[]) => {
                  this.duplicateGridData = [...msiResponse];
                  this.isShowGrid = true;
                  this.loading = false;
                  this.cdRef.detectChanges();
                },
                error: () => {
                  this.isShowGrid = true;
                  this.loading = false;
                  this.cdRef.detectChanges();
                },
              });
            }
            if (
              !response?.isRequestSucessful &&
              !response.error?.startsWith(ErrorMessages.STUDENT_ALREADY_EXISTS)
            ) {
              const notificationRef: NotificationRef =
                this.notificationService.show({
                  content: response.error,
                  cssClass: 'notification-error',
                  position: { horizontal: 'center', vertical: 'top' },
                  animation: { type: 'slide', duration: 60 },
                  type: { style: 'error', icon: false },
                  closable: true,
                });
              this.existingNotification = notificationRef;
              this.loading = false;
            } else if (response.isRequestSucessful && !response.error) {
              this.isShowGrid = false;
              this.loading = false;
              this.postSubmitConfirmation(false, response);
              this.existingNotification?.hide();
            }
          },
          error: (error: HttpErrorResponse) => {
            this.notificationService.show({
              content:
                'Submission Failed' +
                (error?.error?.error ? ': ' + error.error.error : ''),
              cssClass: 'notification-error',
              hideAfter: 5000,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: 200 },
              type: { style: 'error', icon: true },
            });

            this.isShowGrid = false;
            this.loading = false;
          },
        });
      });
    }
  }
  allowCityCharacters(event: KeyboardEvent): void {
    const allowedCharacters: RegExp = /^[a-zA-Z\s-]$/;
    const key: string = event.key;
    if (!allowedCharacters.test(key)) {
      event.preventDefault();
    }
  }

  public toggle(id: string): void {
    if (this.show === id) {
      this.show = '';
    } else {
      this.show = id;
    }
  }

  public openMenu(dataItem: StudentInfo, rowIndex: number): void {
    this.duplicateStudent = {
      ...dataItem,
      rowIndex,
      actions: [...this.getContextMenuItems(dataItem)],
    };
    const targetButton: ElementRef<HTMLButtonElement> = this.anchorRefs
      .toArray()
      ?.at(rowIndex);

    if (targetButton) {
      this.gridContextMenu.show(targetButton);
    }
  }

  getContextMenuItems(dataItem: StudentInfo): { text: string }[] {
    const items: { text: string }[] = [];

    if (dataItem.isPreSchool) {
      items.push({ text: 'View student profile' });
    }

    if (
      (!!dataItem.refStatus || dataItem.refStatus === this.newReferralStatus) &&
      dataItem.nycId &&
      !dataItem.hasIEP &&
      !dataItem.ineligibleSE &&
      dataItem.isPreSchool
    ) {
      items.push({ text: 'Continue to the new referral screen' });
    }

    return items;
  }

  onSelect(menuItem: ContextMenuSelectEvent): void {
    switch (menuItem.item.text) {
      case 'Continue to the new referral screen':
        this.openInitReferralRequest(this.duplicateStudent);
        break;
      case 'View student profile':
        this.openStudentProfile(this.duplicateStudent);
        break;
    }
  }

  openInitReferralRequest(profile: StudentInfo): void {
    if (profile.dateOfBirth !== null) {
      const dob: Date = new Date(profile.dateOfBirth);
      const dobYear: number = dob.getFullYear();
      profile.isEI = this.isEISearch;
      this.studentStore.initializeSelectedStudent(profile);

      if (
        this.maxDOB <
        new Date(dobYear + this.threeYears, dob.getMonth(), dob.getDate())
      ) {
        const isEI: boolean = profile.nycId?.length === this.nycIdLength;
        if (dob.getMonth() < this.july) {
          // Months defined at the top of the file
          const rDate: string = isEI ? 'September 4, ' : 'October 1, ';
          if (
            this.maxDOB <
            new Date(
              dobYear + this.twoYears,
              isEI ? this.september : this.october,
              isEI ? this.dayFour : this.dayOne
            )
          ) {
            const errorMessage: string =
              'The child will be eligible for an Initial Referral starting ' +
              rDate +
              (dobYear + this.twoYears);
            this.notificationService.show({
              content: errorMessage,
              hideAfter: 5000,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              type: { style: 'error', icon: true },
            });
          } else {
            this.openInitialReferralRequest(profile);
          }
        } else {
          const rDate: string = isEI ? 'March 4, ' : 'April 1, ';
          if (
            this.maxDOB <
            new Date(
              dobYear + this.threeYears,
              isEI ? this.march : this.april,
              isEI ? this.dayFour : this.dayOne
            )
          ) {
            const errorMessage: string =
              'The child will be eligible for an Initial Referral starting ' +
              rDate +
              (dobYear + this.threeYears);
            this.notificationService.show({
              content: errorMessage,
              hideAfter: 5000,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              type: { style: 'error', icon: true },
            });
          } else {
            this.openInitialReferralRequest(profile);
          }
        }
      } else {
        this.openInitialReferralRequest(profile);
      }
    } else {
      this.notificationService.show({
        content: 'Date of Birth missing on Student record',
        hideAfter: 600,
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        type: { style: 'error', icon: true },
      });
    }
  }

  openInitialReferralRequest(profile: StudentInfo): void {
    const route: string =
      this.routesConstantService.routes.newReferralsRequestPage;
    this.router.navigate([`${route}/${profile?.nycId}`]);
    this.closeModal.next();
  }

  openStudentProfile(profile: StudentInfo): void {
    this.studentStore.initializeSelectedStudent(profile);
    this.router.navigate([
      this.routesConstantService.routes.studentInformationPage,
      profile.nycId,
    ]);
    this.closeModal.next();
  }

  setSchoolFields(): void {
    if (this.user && this.referenceData) {
      /* empty */
    }
  }

  get entity$(): Observable<StudentSearchNewStudentRequest> {
    return of();
  }

  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([
      this.referenceStore.sex$,
      this.referenceStore.borough$,
      this.referenceStore.state$,
      this.referenceStore.district$,
      this.referenceStore.locations$,
      this.referenceStore.relationship$,
      this.referenceStore.language$,
    ]);
  }

  get canSaveDraft(): boolean {
    return !this.form.valid;
  }

  get isHomeDistrictRequired(): boolean {
    const boroughId: number = this.form.get('homeBorough')?.value?.id as number;
    return (
      boroughId >= this.bronxBoroughId &&
      boroughId <= this.statenIslandBoroughId
    );
  }

  resetForm(): void {
    this.form.reset();
  }

  getExpansionPanelTitle(): string {
    const guardianFirstName: string =
      this.form.get('guardianFirstName')?.value ?? '';
    const guardianLastName: string =
      this.form.get('guardianLastName')?.value ?? '';
    const guardianRelationship: string =
      this.form.get('guardianRelationship')?.value?.description ?? '';

    return (
      guardianFirstName +
      ' ' +
      guardianLastName +
      (guardianRelationship ? ' (' + guardianRelationship + ')' : '')
    );
  }
}
